import React from 'react';
import { FiEdit, FiEye, FiMoreVertical } from 'react-icons/fi';
import { Customer } from '../../../hooks/useCustomers';
import { BatchCheckbox } from '../../../components/BatchCheckbox';
import { formatCurrency } from '../../../utils/formatCurrency';
import { createPortal } from 'react-dom';

interface CustomerRowProps {
  customer: Customer;
  onEdit: (customer: Customer) => void;
  onView: (customer: Customer) => void;
  onDelete?: (customer: Customer) => void;
  currentBalance?: number;
}

export const CustomerRow: React.FC<CustomerRowProps> = ({
  customer,
  onEdit,
  onView,
  onDelete,
  currentBalance: currentBalanceProp,
}) => {
  const [showMenu, setShowMenu] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const menuButtonRef = React.useRef<HTMLButtonElement | null>(null);

  // Use precomputed balance when provided; default to 0 with no per-row fetching
  const displayedBalance = Number(currentBalanceProp ?? 0);

  const getCustomerDisplayName = (customer: Customer) => {
    if (customer.display_name) return customer.display_name;
    if (customer.company_name) return customer.company_name;
    const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    return fullName || 'Unnamed Customer';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onView(customer);
    }
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      toggleMenu();
    } else if (e.key === 'Escape') {
      setShowMenu(false);
    }
  };

  const toggleMenu = () => {
    if (!showMenu) {
      const rect = menuButtonRef.current?.getBoundingClientRect();
      if (rect) {
        const menuWidth = 184; // approximate min width
        const gap = 8;
        const left = Math.min(
          Math.max(gap, rect.right - menuWidth),
          window.innerWidth - menuWidth - gap
        );
        const top = Math.min(rect.bottom + 4, window.innerHeight - gap);
        setMenuPos({ top, left });
      }
    }
    setShowMenu(!showMenu);
  };

  React.useEffect(() => {
    if (!showMenu) return;
    const onScroll = () => setShowMenu(false);
    const onResize = () => setShowMenu(false);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [showMenu]);

  return (
    <tr 
      className="group relative transition-colors odd:bg-white even:bg-gray-50/40 hover:bg-sedra/5 focus-within:bg-sedra/5 dark:odd:bg-neutral-900 dark:even:bg-neutral-900/60 cursor-pointer"
      onClick={() => onView(customer)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View details for ${getCustomerDisplayName(customer)}`}
    >
      {/* Batch Selection Checkbox */}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center">
          <BatchCheckbox 
            id={customer.id}
            className="w-4 h-4"
          />
        </div>
      </td>
      {/* Customer Name */}
      <td className="px-4 py-3">
        <div className="text-sm font-medium text-gray-900 dark:text-neutral-50">
          {getCustomerDisplayName(customer)}
        </div>
        {customer.company_name && customer.display_name && (
          <div className="text-xs text-gray-500 dark:text-neutral-400">
            {customer.company_name}
          </div>
        )}
      </td>

      {/* Email */}
      <td className="px-4 py-3">
        <div className="text-sm text-gray-700 dark:text-neutral-300 truncate">
          {customer.email}
        </div>
      </td>

      {/* Phone */}
      <td className="px-4 py-3">
        <div className="text-sm text-gray-700 dark:text-neutral-300">
          {customer.phone || '—'}
        </div>
      </td>

      {/* Location */}
      <td className="px-4 py-3">
        <div className="text-sm text-gray-700 dark:text-neutral-300">
          {customer.billing_addr_city && customer.billing_addr_state 
            ? `${customer.billing_addr_city}, ${customer.billing_addr_state}`
            : '—'
          }
        </div>
      </td>

      {/* Created Date */}
      <td className="px-4 py-3">
        <div className="text-sm text-gray-500 dark:text-neutral-400">
          {customer.created_at ? formatDate(customer.created_at) : 'N/A'}
        </div>
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onView(customer);
            }}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
            title="View Details"
            aria-label={`View details for ${getCustomerDisplayName(customer)}`}
          >
            <FiEye size={16} aria-hidden="true" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(customer);
            }}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
            title="Edit Customer"
            aria-label={`Edit ${getCustomerDisplayName(customer)}`}
          >
            <FiEdit size={16} aria-hidden="true" />
          </button>
          <div className="relative">
            <button
              ref={menuButtonRef}
              onClick={(e) => {
                e.stopPropagation();
                toggleMenu();
              }}
              onKeyDown={handleMenuKeyDown}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
              title="More Options"
              aria-label={`More options for ${getCustomerDisplayName(customer)}`}
              aria-expanded={showMenu}
              aria-haspopup="true"
            >
              <FiMoreVertical size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      </td>

      {/* Current Balance (rightmost) */}
      <td className="px-4 py-3 text-right">
        <div className="text-sm font-medium text-gray-900 dark:text-neutral-50 tabular-nums">
          {formatCurrency(displayedBalance)}
        </div>
      </td>

      {/* Portal-based dropdown menu & backdrop */}
      {showMenu && createPortal(
        <>
          <div 
            className="fixed inset-0 z-[999]" 
            onClick={() => setShowMenu(false)}
            role="presentation"
            aria-hidden="true"
          />
          <div 
            className="fixed z-[1000] bg-white dark:bg-neutral-900 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-1 min-w-[180px]"
            style={{ top: menuPos.top, left: menuPos.left }}
            role="menu"
            aria-orientation="vertical"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView(customer);
                setShowMenu(false);
              }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800 focus:outline-none"
              role="menuitem"
            >
              View Details
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(customer);
                setShowMenu(false);
              }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800 focus:outline-none"
              role="menuitem"
            >
              Edit Customer
            </button>
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(customer);
                  setShowMenu(false);
                }}
                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-gray-50 dark:hover:bg-neutral-800 focus:outline-none"
                role="menuitem"
              >
                Delete Customer
              </button>
            )}
          </div>
        </>,
        document.body
      )}
    </tr>
  );
}; 